import * as React from 'react';
import { connect } from 'react-redux';

import CampaignBriefForm from 'modules/campaigns/components/campaign-brief-form/CampaignBriefForm';

import { editCampaignBrief, fetchCampaignBrief, storeBriefDetails } from 'modules/campaigns/reducers/campaignBrief';
import { receiveAssets, saveAssets } from 'modules/campaigns/reducers/campaignAssets';
import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { logError } from 'utils/log';
import { t } from 'utils/language';
import { getTotalOffersNumber } from 'utils/campaign';

import type { Campaign, CampaignAsset, CampaignForm, CampaignOverview, PromiseAction } from 'types';

type OwnProps = {
  campaignId: number,
  campaignOverview: CampaignOverview,
  askToSendNotifications: boolean,
  onCancel: () => PromiseAction,
  onSubmit: () => Promise<any>
};

type StateProps = {
  campaign: Campaign,
  campaignAssets: {
    assets: Array<CampaignAsset>,
    mainImage: CampaignAsset | undefined | null,
    isUpdated: boolean
  }
};

type DispatchProps = {
  dispatchFetchCampaignBrief: (campaignId: number, showLoader?: boolean) => Promise<void>,
  dispatchStoreBriefDetails: (briefDetails: CampaignForm) => Promise<void>,
  dispatchEditCampaignBrief: (shouldSendNotifications: boolean) => Promise<void>,
  dispatchSaveAssets: (campaignId: number) => Promise<void>,
  dispatchReceiveAssets: (assets: Array<CampaignAsset>, mainImage?: CampaignAsset | null) => Promise<void>,
  dispatchShowConfirmationModal: (
    params: {
      title: string,
      message: string,
      cancelButtonLabel: string,
      confirmButtonLabel: string,
      onConfirm: () => void,
      onCancel: () => void,
      onClose: () => void
    }
  ) => void,
  dispatchHideConfirmationModal: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

class CampaignEdit extends React.PureComponent<Props> {
  componentDidMount(): void {
    const { dispatchFetchCampaignBrief, campaignId } = this.props;
    if (this.isCampaignBriefMissing()) {
      dispatchFetchCampaignBrief(campaignId, true);
    }
  }

  componentWillUnmount() {
    this.resetImages();
  }

  isCampaignBriefMissing(): boolean {
    const { campaign, campaignId } = this.props;
    return !campaign.id || campaign.id !== campaignId;
  }

  resetImages(): Promise<void> {
    const { campaign, dispatchReceiveAssets } = this.props;

    // restore unmodified images
    return dispatchReceiveAssets(campaign.assets || [], campaign.mainImage || null);
  }

  handleCancel = (): PromiseAction => this.props.onCancel();

  submitChanges(briefDetails: CampaignForm, shouldSendNotifications: boolean): PromiseAction {
    const {
      campaignId,
      onSubmit,
      dispatchStoreBriefDetails,
      dispatchEditCampaignBrief,
      dispatchFetchCampaignBrief,
      dispatchSaveAssets
    } = this.props;

    return Promise.resolve(dispatchStoreBriefDetails(briefDetails))
      .then(() => dispatchEditCampaignBrief(shouldSendNotifications))
      .then(() => { // eslint-disable-line arrow-body-style
        return dispatchSaveAssets(campaignId)
          .catch((error) => { logError(error); }) // no images is okayish
          .then(() => dispatchFetchCampaignBrief(campaignId))
          .then(onSubmit);
      }) // $FlowIgnore
      .catch(() => this.scrollToErrorMessage());
  }

  handleSubmit = (briefDetails: CampaignForm, isUpdated: boolean): PromiseAction => {
    const {
      campaign,
      campaignAssets,
      campaignOverview,
      askToSendNotifications,
      dispatchShowConfirmationModal,
      dispatchHideConfirmationModal
    } = this.props;
    const isExternalBriefUpdated = isUpdated || campaignAssets.isUpdated;

    if (!isExternalBriefUpdated) {
      return this.handleCancel();
    }

    const isOnlyInternalFieldUpdated = !campaignAssets.isUpdated
      && campaign.description === briefDetails.description
      && campaign.additionalRequirementsTemplate === briefDetails.additionalRequirementsTemplate
      && campaign.endDate === briefDetails.endDate
      && campaign.startDate === briefDetails.startDate
      && campaign.name === briefDetails.name
      && campaign.budget !== briefDetails.budget;

    if (!askToSendNotifications || isOnlyInternalFieldUpdated || getTotalOffersNumber(campaignOverview) === 0) {
      return this.submitChanges(briefDetails, false);
    }

    const isAdditionalRequirementsTemplateUpdated = campaign.additionalRequirementsTemplate !== briefDetails.additionalRequirementsTemplate;

    if (isAdditionalRequirementsTemplateUpdated) {
      return new Promise((resolve: (b: boolean) => void, reject) => {
        dispatchShowConfirmationModal({
          title: t('campaign_external_brief_and_additional_requirements_updated_modal_title'),
          message: t('campaign_external_brief_and_additional_requirements_updated_modal_message'),
          cancelButtonLabel: t('action_button_cancel'),
          confirmButtonLabel: t('action_button_save_and_send'),
          onConfirm: () => {
            dispatchHideConfirmationModal();
            resolve(false);
          },
          onCancel: () => {
            dispatchHideConfirmationModal();
            reject();
          },
          onClose: () => {
            dispatchHideConfirmationModal();
            reject();
          }
        });
      })
        .then(shouldSendNotifications => this.submitChanges(briefDetails, shouldSendNotifications))
        .catch(() => {
          /* onClose handled here */
          const pseudoReturn = Promise.resolve();

          return pseudoReturn as any as PromiseAction;
        });
    }

    return new Promise((resolve: (b: boolean) => void, reject) => {
      dispatchShowConfirmationModal({
        title: t('campaign_external_brief_updated_modal_title'),
        message: t('campaign_external_brief_updated_modal_message'),
        cancelButtonLabel: t('action_button_save'),
        confirmButtonLabel: t('action_button_save_and_send'),
        onConfirm: () => {
          dispatchHideConfirmationModal();
          resolve(true);
        },
        onCancel: () => {
          dispatchHideConfirmationModal();
          resolve(false);
        },
        onClose: () => {
          dispatchHideConfirmationModal();
          reject();
        }
      });
    })
      .then((shouldSendNotifications) => this.submitChanges(briefDetails, shouldSendNotifications))
      .catch(() => {
        /* onClose handled here */
        const pseudoReturn = Promise.resolve();

        return pseudoReturn as any as PromiseAction;
      });
  };

  scrollToErrorMessage(): void {
    window.scrollTo(0, 0);
  }

  render() {
    const { campaignAssets, campaignOverview, campaign } = this.props;

    if (this.isCampaignBriefMissing()) {
      return null;
    }

    return (
      <CampaignBriefForm
        inline
        campaignAssets={campaignAssets}
        campaign={campaign}
        campaignOverview={campaignOverview}
        onSubmit={this.handleSubmit}
        onCancel={this.handleCancel}
      />
    );
  }
}

const mapStateToProps = ({ campaignBrief, campaignAssets }): StateProps => ({
  campaign: campaignBrief.brief,
  campaignAssets
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchCampaignBrief: (campaignId: number, showLoader: boolean = false) => dispatch(fetchCampaignBrief(campaignId, showLoader, false)),
  dispatchStoreBriefDetails: (briefDetails: CampaignForm) => dispatch(storeBriefDetails(briefDetails)),
  dispatchEditCampaignBrief: (shouldSendNotifications: boolean) => dispatch(editCampaignBrief(shouldSendNotifications)),
  dispatchSaveAssets: (campaignId: number) => dispatch(saveAssets(campaignId)),
  dispatchReceiveAssets: (assets: Array<CampaignAsset>, mainImage?: CampaignAsset | null) => dispatch(receiveAssets(assets, mainImage)),
  dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal())
});

export default connect(mapStateToProps, mapDispatchToProps)(CampaignEdit);
